<script setup lang="ts">
import { useI18n } from '@/i18n';
import { useAccountsStore } from '@/stores/accounts';
import { useUserStore } from '@/stores/user';
import { Confirm, Loader, Prompt, Toaster, useToastStore } from '@dabble/quillui';
import { storeToRefs } from 'pinia';
import posthog from 'posthog-js';
import { onBeforeMount, watch } from 'vue';

const { t } = useI18n();
const isDev = import.meta.env.DEV;

const toastStore = useToastStore();
toastStore.updateConfig({ position: 'top-center' });

const { userDataReady } = storeToRefs(useAccountsStore());
const userStore = useUserStore();

let identifiedUid = '';
watch(
  () => ({ uid: userStore.user.uid, email: userStore.user.email, name: userStore.user.name }),
  ({ uid, email, name }) => {
    if (uid && email && uid !== identifiedUid) {
      posthog.identify(uid, { email, name });
      identifiedUid = uid;
    }
  },
  { immediate: true },
);

onBeforeMount(() => {
  document.documentElement.dataset.theme = 'light';
});
</script>

<template>
  <div class="app">
    <div v-if="!userDataReady" class="app__loader" data-testid="app-loader">
      <Loader />
    </div>
    <template v-else>
      <router-view v-slot="{ Component }">
        <transition name="fade" mode="out-in">
          <component :is="Component" />
        </transition>
      </router-view>
      <Toaster />
      <Prompt />
      <Confirm />
    </template>
    <div v-if="isDev" class="local-badge">{{ t('app_local_badge') }}</div>
  </div>
</template>

<style lang="scss" scoped>
.app {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  min-height: 100vh;
  min-height: 100dvh;
  background-color: theme('color-bg-app-overlay');
  color: theme('color-text-primary');

  &__loader {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 100vh;
    min-height: 100dvh;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.local-badge {
  position: fixed;
  bottom: var(--space-tiny);
  left: var(--space-tiny);
  background: var(--color-brand-red);
  color: var(--color-text-primary-inverted);
  font-size: var(--font-size-xsmall);
  font-weight: var(--font-weight-bold);
  padding: var(--space-xtiny) var(--space-xxxsmall);
  border-radius: var(--border-radius-tiny);
  z-index: 9999;
  pointer-events: none;
}
</style>
